import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import '../styles/Onboarding.css';

import giftBear from '../assets/gifts/Toy Bear.webp';
import giftRing from '../assets/gifts/Diamond Ring.webp';
import giftPepe from '../assets/gifts/Plush Pepe.webp';
import giftWatch from '../assets/gifts/Swiss Watch.webp';

interface GiftOption {
  id: number;
  name: string;
  img: string;
}

const GIFTS: GiftOption[] = [
  { id: 1, name: 'Toy Bear', img: giftBear },
  { id: 2, name: 'Diamond Ring', img: giftRing },
  { id: 3, name: 'Plush Pepe', img: giftPepe },
  { id: 4, name: 'Swiss Watch', img: giftWatch },
];

const GRID_SIZE = 4;
const TOTAL_CELLS = GRID_SIZE * GRID_SIZE;

type CellState = 'hidden' | 'bomb' | 'gift';
type OnboardingStep = 'select' | 'hiding' | 'game' | 'result' | 'popup_no_attempts';

const OnboardingFlow: React.FC<{ onComplete: () => void; onPromoClick?: () => void }> = ({
  onComplete,
  onPromoClick,
}) => {
  const [step, setStep] = useState<OnboardingStep>('select');
  const [selectedGift, setSelectedGift] = useState<GiftOption | null>(null);
  const [grid, setGrid] = useState<CellState[]>(Array(TOTAL_CELLS).fill('hidden'));
  const [isProcessing, setIsProcessing] = useState(false);
  const [bombIndex, setBombIndex] = useState<number | null>(null);
  const { completeOnboarding } = useGameStore();

  // Gift is always at a secret fixed position, revealed only after losing
  const giftCellIndex = useRef(Math.floor(Math.random() * TOTAL_CELLS));

  const handleSelectGift = useCallback((gift: GiftOption) => {
    setSelectedGift(gift);
  }, []);

  const handleConfirmGift = useCallback(() => {
    if (!selectedGift) return;
    setStep('hiding');
  }, [selectedGift]);

  useEffect(() => {
    if (step !== 'hiding') return;
    const timer = setTimeout(() => setStep('game'), 1400);
    return () => clearTimeout(timer);
  }, [step]);

  const handleCellClick = useCallback((index: number) => {
    if (isProcessing || step !== 'game' || grid[index] !== 'hidden') return;
    setIsProcessing(true);

    const newGrid: CellState[] = Array(TOTAL_CELLS).fill('hidden');
    newGrid[index] = 'bomb';
    // Ensure gift cell differs from bomb cell
    const giftIdx = giftCellIndex.current === index
      ? (index + 1) % TOTAL_CELLS
      : giftCellIndex.current;
    newGrid[giftIdx] = 'gift';

    setBombIndex(index);
    setGrid(newGrid);

    setTimeout(() => {
      setStep('result');
      setIsProcessing(false);
    }, 700);
  }, [isProcessing, grid, step]);

  const handleRetry = useCallback(() => {
    setStep('popup_no_attempts');
  }, []);

  const handleFinish = useCallback(async () => {
    await completeOnboarding();
    onComplete();
  }, [completeOnboarding, onComplete]);

  const promoBanner = (
    <div className="onb-top">
      <button
        className="onb-promo-banner"
        type="button"
        onClick={onPromoClick}
        disabled={!onPromoClick}
      >
        <div className="onb-promo-content">
          <div className="onb-promo-title">✨ Получи 1000 звёзд Telegram!</div>
          <div className="onb-promo-subtitle">Нажми, чтобы узнать как!</div>
        </div>
        <img className="onb-promo-image" src="/img/Plush Pepe.webp" alt="Plush Pepe" />
      </button>
    </div>
  );

  const renderWithTop = (content: React.ReactNode) => (
    <div className="onb-overlay">
      {promoBanner}
      <div className="onb-stage">{content}</div>
    </div>
  );

  /* ---- SELECT SCREEN ---- */
  if (step === 'select') {
    return renderWithTop(
      <div className="onb-select">
        <div className="onb-title">🎁 Выбери подарок!</div>
        <div className="onb-subtitle">Один из них спрятан на поле — найди его и выиграй!</div>
        <div className="onb-gifts-grid">
          {GIFTS.map((gift) => (
            <button
              key={gift.id}
              type="button"
              className={`onb-gift-card${selectedGift?.id === gift.id ? ' onb-gift-card--sel' : ''}`}
              onClick={() => handleSelectGift(gift)}
            >
              <img src={gift.img} alt={gift.name} draggable={false} />
              <span>{gift.name}</span>
              {selectedGift?.id === gift.id && <div className="onb-gift-card-ring" />}
            </button>
          ))}
        </div>
        <button className="onb-cta-btn" disabled={!selectedGift} onClick={handleConfirmGift}>
          Далее →
        </button>
      </div>,
    );
  }

  /* ---- HIDING ANIMATION ---- */
  if (step === 'hiding') {
    return renderWithTop(
      <div className="onb-game-wrap">
        <div className="onb-game-header">
          <img className="onb-header-img" src={selectedGift!.img} alt="" draggable={false} />
          <div className="onb-header-text">
            Прячем <b>{selectedGift!.name}</b> на поле…
          </div>
        </div>
        <div className="onb-board">
          {Array(TOTAL_CELLS).fill(null).map((_, i) => (
            <div key={i} className="onb-cell" />
          ))}
          <div className="onb-flying-gift">
            <img src={selectedGift!.img} alt="" draggable={false} />
          </div>
        </div>
      </div>,
    );
  }

  /* ---- GAME / RESULT SCREENS (same grid stays rendered) ---- */
  if (step === 'game' || step === 'result') {
    return renderWithTop(
      <div className="onb-game-wrap">
        <div className="onb-game-header">
          <img className="onb-header-img" src={selectedGift!.img} alt="" draggable={false} />
          <div className="onb-header-text">
            {step === 'game' ? (
              <>
                <span>Найди</span> <b>{selectedGift!.name}</b> на поле!
              </>
            ) : (
              <>
                <b>Был здесь!</b> Но ты нарвался на мину 💣
              </>
            )}
          </div>
        </div>

        <div className="onb-board">
          {grid.map((cell, i) => (
            <div key={i} className="onb-cell-wrap">
              <button
                type="button"
                className={`onb-cell${cell === 'bomb' ? ' onb-cell--bomb' : cell === 'gift' ? ' onb-cell--gift' : ''}${
                  step === 'result' ? ' onb-cell--no-hover' : ''
                }`}
                onClick={step === 'game' ? () => handleCellClick(i) : undefined}
                disabled={step === 'result'}
              >
                {cell === 'bomb' && <span className="onb-icon">💣</span>}
                {cell === 'gift' && (
                  <img className="onb-cell-gift-img" src={selectedGift!.img} alt="" draggable={false} />
                )}
              </button>
            </div>
          ))}
        </div>

        {step === 'result' && (
          <div className="onb-result-bar">
            <div className="onb-result-title">💣 Не повезло!</div>
            <div className="onb-result-text">Подарок был вон там! Хочешь попробовать ещё раз?</div>
            <button className="onb-cta-btn" onClick={handleRetry}>
              Попробовать ещё раз
            </button>
          </div>
        )}
      </div>,
    );
  }

  /* ---- NO ATTEMPTS POPUP ---- */
  if (step === 'popup_no_attempts') {
    return renderWithTop(
      <div className="onb-final-card">
        <div className="onb-final-icon">🎟</div>
        <div className="onb-final-title">Нет попыток</div>
        <div className="onb-final-text">
          У тебя закончились попытки. Выполняй простые задания, чтобы заработать билетики и найти подарок!
        </div>
        <button className="onb-cta-btn onb-cta-btn--green" onClick={handleFinish}>
          Перейти к заданиям 🚀
        </button>
      </div>,
    );
  }

  return null;
};

export default OnboardingFlow;
